/**
 * Wizard Step 1 — Property details.
 */
import { Card } from "@/components/ui/card";
import { normalizeVirtualTourUrl } from "@/lib/virtual-tour-url";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUploader } from "@/components/admin/v2/ImageUploader";
import { BilingualTextarea } from "@/components/admin/v2/BilingualTextarea";
import { useWizard } from "./wizardContext";
import {
  COLLECTION_MODES,
  PROPERTY_TYPES,
  REVENUE_MODELS,
  type CollectionMode,
  type RevenueModel,
} from "@/types/property";
import { validateStreetName } from "@/lib/address";
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete";
import { REVENUE_MODEL_LABELS } from "@/lib/revenue-model";
import { collectionModeLabels } from "@/lib/collection-mode";
import { useAuth } from "@/contexts/AuthContext";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { useLanguage } from "@/contexts/LanguageContext";

export function Step1Property() {
  const { state, setProperty } = useWizard();
  const { isOwnerPortalOnly } = useAuth();
  const { platformName } = useOperatorBranding();
  const { t } = useLanguage();
  const COLLECTION_MODE_LABELS = collectionModeLabels(null, platformName, t);
  const p = state.property;
  const streetNameError = validateStreetName(p.street_name);

  return (
    <Card className="p-6 space-y-4">
      <header>
        <h2 className="text-lg font-semibold">
          {t("admin.wizard.step1.title", "Property details")}
        </h2>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t("admin.wizard.step1.titleField", "Title")}
          required
          className="sm:col-span-2"
        >
          <Input
            value={p.title}
            onChange={(e) => setProperty({ title: e.target.value })}
            placeholder={t(
              "admin.wizard.step1.titlePlaceholder",
              "e.g. Sunny WG in Schwabing"
            )}
          />
        </Field>

        <Field
          label={t("admin.wizard.step1.streetName", "Street name")}
          required
          error={streetNameError}
          hint={t(
            "admin.wizard.step1.streetNameHint",
            "Spelled out in full — this is printed on the contract and the Wohnungsgeberbestätigung."
          )}
        >
          <AddressAutocomplete
            value={p.street_name}
            onChange={(v) =>
              // Typing invalidates whatever point the last pick geocoded —
              // otherwise a coordinate for a DIFFERENT address the user has
              // since edited away from gets submitted as if it still matched.
              setProperty({ street_name: v, lat: null, lng: null })
            }
            onSelect={({ street, houseNumber, postalCode, city, lat, lng }) => {
              // The suggestion wins, but a number already on the form is kept.
              // `address` is derived from these two by setProperty, so it is
              // not set here (see composeStreetAddress).
              setProperty({
                street_name: street,
                street_number: houseNumber || p.street_number,
                city: city || p.city,
                zip_code: postalCode || p.zip_code,
                // Keep whatever we already had if this pick came back without
                // a usable point, rather than blanking a good geocode.
                lat: lat ?? p.lat,
                lng: lng ?? p.lng,
              });
            }}
            placeholder="e.g. Baierbrunnerstrasse"
            aria-invalid={!!streetNameError}
          />
        </Field>

        <Field
          label={t("admin.wizard.step1.streetNumber", "Street number")}
          required
        >
          <Input
            value={p.street_number}
            onChange={(e) =>
              setProperty({
                street_number: e.target.value,
                lat: null,
                lng: null,
              })
            }
            placeholder="e.g. 1"
          />
        </Field>

        <Field label={t("admin.wizard.step1.city", "City")} required>
          <Input
            value={p.city}
            onChange={(e) =>
              setProperty({ city: e.target.value, lat: null, lng: null })
            }
            placeholder="München"
          />
        </Field>

        <Field
          label={t("admin.wizard.step1.postalCode", "Postal code (PLZ)")}
          required
        >
          <Input
            value={p.zip_code}
            onChange={(e) =>
              setProperty({ zip_code: e.target.value, lat: null, lng: null })
            }
            inputMode="numeric"
            maxLength={10}
            placeholder="80331"
          />
        </Field>

        <Field label={t("admin.wizard.step1.propertyType", "Property type")}>
          <Select
            value={p.type || undefined}
            onValueChange={(v) =>
              setProperty({ type: v as (typeof PROPERTY_TYPES)[number] })
            }
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t(
                  "admin.wizard.step1.selectTypePlaceholder",
                  "Select type…"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((pt) => (
                <SelectItem key={pt} value={pt}>
                  {pt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label={t("admin.wizard.step1.apartmentCount", "Apartment count")}
        >
          <Input
            type="number"
            min={1}
            value={p.aptCount}
            onChange={(e) => setProperty({ aptCount: e.target.value })}
            placeholder="1"
          />
        </Field>

        {p.aptCount.trim() === "1" && (
          <Field
            label={t(
              "admin.wizard.step1.estimatedRoomCount",
              "Estimated room count"
            )}
            hint={t(
              "admin.wizard.step1.estimatedRoomCountHint",
              "For this single apartment — pre-fills its room list in Step 4."
            )}
          >
            <Input
              type="number"
              min={0}
              value={p.estimatedRoomCount}
              onChange={(e) =>
                setProperty({ estimatedRoomCount: e.target.value })
              }
              placeholder="5"
            />
          </Field>
        )}

        {!isOwnerPortalOnly && (
          <div className="sm:col-span-2 pt-2 border-t space-y-3">
            <h3 className="text-sm font-semibold">
              {t("admin.wizard.step1.revenueModel", "Revenue model")}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t(
                  "admin.wizard.step1.collectionMode",
                  "Collection mode"
                )}
              >
                <Select
                  value={p.collectionMode}
                  onValueChange={(v) =>
                    setProperty({ collectionMode: v as CollectionMode })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLECTION_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {COLLECTION_MODE_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t("admin.wizard.step1.revenueModel", "Revenue model")}
              >
                <Select
                  value={p.revenueModel}
                  onValueChange={(v) =>
                    setProperty({ revenueModel: v as RevenueModel })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REVENUE_MODELS.map((rm) => (
                      <SelectItem key={rm} value={rm}>
                        {REVENUE_MODEL_LABELS[rm]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {p.revenueModel === "full_rent" && (
                <Field
                  label={t(
                    "admin.wizard.step1.masterRent",
                    "Master rent (€/mo)"
                  )}
                >
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={p.masterRent}
                    onChange={(e) =>
                      setProperty({ masterRent: e.target.value })
                    }
                    className="tabular-nums"
                  />
                </Field>
              )}
              {p.revenueModel === "pct_of_rent" && (
                <Field
                  label={`${platformName} ${t("admin.wizard.step1.feePercentSuffix", "fee (%)")}`}
                >
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    inputMode="decimal"
                    value={p.coliviqFeePct}
                    onChange={(e) =>
                      setProperty({ coliviqFeePct: e.target.value })
                    }
                    placeholder="25"
                    className="tabular-nums"
                  />
                </Field>
              )}
              {p.revenueModel === "flat_fee_per_room" && (
                <Field
                  label={t(
                    "admin.wizard.step1.flatFeePerRoom",
                    "Flat fee (€ / room)"
                  )}
                >
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={p.coliviqFeeAmount}
                    onChange={(e) =>
                      setProperty({ coliviqFeeAmount: e.target.value })
                    }
                    placeholder="120"
                    className="tabular-nums"
                  />
                </Field>
              )}
            </div>
          </div>
        )}

        <Field
          label={t("admin.wizard.step1.description", "Description")}
          hint={t(
            "admin.wizard.step1.descriptionHint",
            "Shown to tenants on the public listing. Provide both EN and DE so the German site doesn't fall back to English copy."
          )}
          className="sm:col-span-2"
        >
          <BilingualTextarea
            valueEn={p.description}
            valueDe={p.descriptionDe}
            onChange={({ en, de }) =>
              setProperty({ description: en, descriptionDe: de })
            }
            rows={4}
            placeholder={t(
              "admin.wizard.step1.descriptionPlaceholder",
              "Welcome message, neighborhood description, key amenities…"
            )}
          />
        </Field>

        <div className="sm:col-span-2 pt-2 border-t">
          <ImageUploader
            label={t("admin.wizard.step1.propertyPhotos", "Property photos")}
            hint={t(
              "admin.wizard.step1.propertyPhotosHint",
              "The first image becomes the cover shown on listings. Drag thumbnails to reorder, or click the star button to make any photo primary."
            )}
            value={p.images}
            onChange={(images) => setProperty({ images })}
            maxImages={20}
          />
        </div>

        <Field
          label={t("admin.wizard.step1.virtualTourUrl", "Virtual tour URL")}
          hint={t(
            "admin.wizard.step1.virtualTourUrlHint",
            "Optional. Matterport, Kuula, YouTube 360°, etc. Shown next to the photo gallery on the public listing."
          )}
          className="sm:col-span-2"
        >
          <Input
            type="url"
            inputMode="url"
            value={p.virtualTourUrl}
            onChange={(e) => setProperty({ virtualTourUrl: e.target.value })}
            onBlur={(e) =>
              setProperty({
                virtualTourUrl: normalizeVirtualTourUrl(e.target.value),
              })
            }
            placeholder="https://my.matterport.com/show/?m=…"
          />
        </Field>
      </div>
    </Card>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  /** Validation message for this field. Replaces the hint while it is set. */
  error?: string | null;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <Label className="flex items-center gap-1 text-xs font-medium">
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
